import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { throttleIfPossible } from "@faire/web--source/common/globals/throttleIfPossible";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { getEstimatedViewportType } from "@faire/web--source/common/user-agent/getEstimatedViewportType";
import { getWindowWidth } from "@faire/web--source/ui/hooks/useObservableWindow";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHandleResize } from "./__internal__/useHandleResize";
import { DEFAULT_MIN_WIDTH_BREAKPOINTS, getViewportValues } from "./viewport-utils/viewport-utils";
export var useViewport = function() {
    var minWidthBreakpoints = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : DEFAULT_MIN_WIDTH_BREAKPOINTS;
    var isSSR = useIsSSR();
    var initialViewport = useMemo(function() {
        if (isSSR) {
            return getViewportValues(getEstimatedViewportType().width, minWidthBreakpoints);
        }
        return getViewportValues(getWindowWidth(), minWidthBreakpoints);
    }, [
        isSSR,
        minWidthBreakpoints
    ]);
    var _useState = _sliced_to_array(useState(initialViewport.isMobile), 2), isMobile = _useState[0], setIsMobile = _useState[1];
    var _useState1 = _sliced_to_array(useState(initialViewport.isTablet), 2), isTablet = _useState1[0], setIsTablet = _useState1[1];
    var _useState2 = _sliced_to_array(useState(initialViewport.isDesktop), 2), isDesktop = _useState2[0], setIsDesktop = _useState2[1];
    var _useState3 = _sliced_to_array(useState(initialViewport.isXLargeDesktop), 2), isXLargeDesktop = _useState3[0], setIsXLargeDesktop = _useState3[1];
    var _useState4 = _sliced_to_array(useState(initialViewport.isXXLargeDesktop), 2), isXXLargeDesktop = _useState4[0], setIsXXLargeDesktop = _useState4[1];
    var _useState5 = _sliced_to_array(useState(initialViewport.isTabletAndAbove), 2), isTabletAndAbove = _useState5[0], setIsTabletAndAbove = _useState5[1];
    var _useState6 = _sliced_to_array(useState(initialViewport.isDesktopAndAbove), 2), isDesktopAndAbove = _useState6[0], setIsDesktopAndAbove = _useState6[1];
    var _useState7 = _sliced_to_array(useState(initialViewport.isXLargeAndAbove), 2), isXLargeAndAbove = _useState7[0], setIsXLargeAndAbove = _useState7[1];
    var _useState8 = _sliced_to_array(useState(initialViewport.isTabletAndBelow), 2), isTabletAndBelow = _useState8[0], setIsTabletAndBelow = _useState8[1];
    var handleResize = useCallback(function() {
        var width = getWindowWidth();
        var viewport = getViewportValues(width, minWidthBreakpoints);
        setIsMobile(viewport.isMobile);
        setIsTablet(viewport.isTablet);
        setIsDesktop(viewport.isDesktop);
        setIsXLargeDesktop(viewport.isXLargeDesktop);
        setIsXXLargeDesktop(viewport.isXXLargeDesktop);
        setIsTabletAndAbove(viewport.isTabletAndAbove);
        setIsDesktopAndAbove(viewport.isDesktopAndAbove);
        setIsXLargeAndAbove(viewport.isXLargeAndAbove);
        setIsTabletAndBelow(viewport.isTabletAndBelow);
    }, [
        minWidthBreakpoints
    ]);
    // SSR correction
    useEffect(function() {
        handleResize();
    }, [
        handleResize
    ]);
    var throttledHandleResize = useMemo(function() {
        return throttleIfPossible(handleResize, 200);
    }, [
        handleResize
    ]);
    useHandleResize(throttledHandleResize);
    return useMemo(function() {
        return {
            isMobile: isMobile,
            isTablet: isTablet,
            isDesktop: isDesktop,
            isXLargeDesktop: isXLargeDesktop,
            isXXLargeDesktop: isXXLargeDesktop,
            isTabletAndAbove: isTabletAndAbove,
            isDesktopAndAbove: isDesktopAndAbove,
            isXLargeAndAbove: isXLargeAndAbove,
            isTabletAndBelow: isTabletAndBelow
        };
    }, [
        isDesktop,
        isDesktopAndAbove,
        isMobile,
        isTablet,
        isTabletAndAbove,
        isTabletAndBelow,
        isXLargeDesktop,
        isXXLargeDesktop,
        isXLargeAndAbove
    ]);
};
